import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { CalendarDays, Loader2, MessageSquareText, ClipboardCheck, UserCheck, GraduationCap } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRES = {
  present: { label: 'P', title: 'Présent', cls: 'bg-emerald-500 text-white' },
  retard: { label: 'R', title: 'Retard', cls: 'bg-amber-400 text-white' },
  absent: { label: 'A', title: 'Absent', cls: 'bg-red-500 text-white' },
};
const VAL = { A: 'bg-emerald-100 text-emerald-800', ECA: 'bg-amber-100 text-amber-800', NA: 'bg-red-100 text-red-800' };

export const ParcoursTimeline = ({ dossierId, candidatId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [onlyFilled, setOnlyFilled] = useState(true);

  useEffect(() => {
    setData(null);
    axios.get(`${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/parcours`).then(r => setData(r.data)).catch(() => setData({ journees: [] }));
  }, [dossierId, candidatId, refreshKey]);

  const journees = (data?.journees || []).filter(j => !onlyFilled || j.observation || j.evaluations.length > 0);

  return (
    <Card className="border-0 shadow-sm" data-testid="parcours-panel">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-indigo-500" /> Parcours jour par jour <span className="text-slate-400 font-normal">({journees.length})</span></h2>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={onlyFilled} onChange={(e) => setOnlyFilled(e.target.checked)} className="rounded" data-testid="parcours-filter-checkbox" />
            Uniquement les jours avec observation ou évaluation
          </label>
        </div>
        {!data ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : journees.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Aucune journée à afficher.</p>
        ) : (
          <ol className="relative border-l border-slate-200 ml-3 space-y-4">
            {journees.map(j => {
              const p = PRES[j.presence];
              return (
                <li key={`${j.date}-${j.via_aft || 'af'}`} className={`ml-5 ${j.apres_abandon ? 'opacity-50' : ''}`} data-testid={`parcours-jour-${j.date}${j.via_aft ? `-${j.via_aft}` : ''}`}>
                  <span className={`absolute -left-[13px] w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center ring-4 ring-white ${p ? p.cls : 'bg-slate-200 text-slate-500'}`} title={p?.title || (j.presence === null ? 'Présence non renseignée' : '')}>{p?.label || '·'}</span>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <p className="text-sm font-semibold text-slate-900 capitalize">{format(parseISO(j.date), 'EEEE d MMMM yyyy', { locale: fr })}</p>
                    {j.via_aft && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-cyan-100 text-cyan-800" title="Journée réalisée sur l'action de certification" data-testid={`parcours-via-aft-${j.date}`}><GraduationCap className="w-3 h-3" /> via {j.via_aft}</span>}
                    {j.demi_journee !== 'journee' && <span className="text-xs text-slate-400">{j.demi_journee === 'matin' ? 'Matin' : 'Après-midi'}</span>}
                    {j.formateur && <span className="text-xs text-slate-500 flex items-center gap-1"><UserCheck className="w-3 h-3" /> {j.formateur}</span>}
                    {j.apres_abandon && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">après abandon</span>}
                  </div>
                  {j.modules.length > 0 && <p className="text-xs text-slate-500 mt-0.5">{j.modules.join(' · ')}</p>}
                  {j.presence === 'retard' && j.heure_arrivee && <p className="text-xs text-amber-600 mt-0.5">Arrivée à {j.heure_arrivee}{j.commentaire_presence ? ` — ${j.commentaire_presence}` : ''}</p>}
                  {j.presence === 'absent' && j.commentaire_presence && <p className="text-xs text-red-600 mt-0.5">Motif : {j.commentaire_presence}</p>}
                  {j.observation && (
                    <div className="mt-2 rounded-lg bg-indigo-50/60 border border-indigo-100 p-2.5 text-sm text-slate-700 flex gap-2" data-testid={`parcours-obs-${j.date}`}>
                      <MessageSquareText className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" /><p className="whitespace-pre-wrap">{j.observation}</p>
                    </div>
                  )}
                  {j.evaluations.map((e, i) => (
                    <div key={i} className="mt-2 rounded-lg bg-white border border-slate-200 p-2.5 text-sm" data-testid={`parcours-eval-${j.date}-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="flex items-center gap-2 text-slate-800"><ClipboardCheck className="w-4 h-4 text-slate-400" /> <span className="text-[10px] uppercase tracking-wide text-slate-400">{e.type}</span> {e.titre}</p>
                        <span className={`font-semibold ${e.pourcentage == null ? 'text-slate-400' : e.pourcentage >= 50 ? 'text-emerald-600' : 'text-red-600'}`}>{e.pourcentage == null ? (e.non_repondu ? 'Non répondu' : '—') : `${Math.round(e.pourcentage)} %`}{e.score != null && e.score_max ? <span className="text-xs text-slate-400 font-normal ml-1">({e.score}/{e.score_max})</span> : null}</span>
                      </div>
                      {e.competences?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {e.competences.map((c, k) => <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded ${VAL[c.valeur] || 'bg-slate-100 text-slate-600'}`}>{c.titre} · {c.valeur || '—'}</span>)}
                        </div>
                      )}
                      {e.commentaire && <p className="text-xs text-slate-500 mt-1">{e.commentaire}</p>}
                    </div>
                  ))}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
